import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { extractStatementEntries, formatWalletBalanceMinor } from '../../services/channelService';
import { getWallet, getWalletInvoice, getWalletStatement } from '../../services/walletPublicService';
import InternalB2BWalletOps from './InternalB2BWalletOps';
import CashWalletReceiveQr from './CashWalletReceiveQr';
import WalletDepositInstructions, { parseDepositInstructionOptions } from './WalletDepositInstructions';
import { useToast } from '../../context/ToastContext';
import { getAxiosErrorMessage } from '../../services/authService';

const NO_NARRATION = 'No narration';

/** Label from `GET …/:wallet_id` (owner_type, owner_id, currency). */
function walletSummaryLabel(data) {
  if (!data || typeof data !== 'object') return '';
  const parts = [];
  if (typeof data.owner_type === 'string' && data.owner_type.trim() !== '') parts.push(data.owner_type.trim());
  if (data.owner_id != null && String(data.owner_id).trim() !== '') parts.push(String(data.owner_id).trim());
  if (typeof data.currency === 'string' && data.currency.trim() !== '') parts.push(data.currency.trim());
  return parts.join(' · ');
}

/** Numeric field from the statement API envelope (minor units). */
function statementEnvelopeMinor(data, keys) {
  if (!data || typeof data !== 'object') return null;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (!(k in data)) continue;
    const v = data[k];
    if (v == null) continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Shorten long IDs in the table; full value stays on `title`. */
function truncateMiddle(str, headChars = 8, tailChars = 6) {
  const s = str == null ? '' : String(str);
  if (s.length <= headChars + tailChars + 1) return s;
  return `${s.slice(0, headChars)}…${s.slice(-tailChars)}`;
}

function metadataAsObject(metadata) {
  if (metadata == null) return null;
  if (typeof metadata === 'string') {
    const t = metadata.trim();
    if (t === '') return null;
    try {
      const parsed = JSON.parse(t);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof metadata === 'object' && !Array.isArray(metadata)) return metadata;
  return null;
}

/** Text shown in the Metadata column: `metadata.narration` (object or JSON string metadata). */
/** Non-empty order id from parsed `metadata` (object or JSON string), if present. */
function extractOrderIdFromRow(row) {
  const meta = metadataAsObject(row?.metadata);
  if (!meta) return null;
  const raw = meta.order_id;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s === '' ? null : s;
}

function extractReferenceIdFromRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  const raw = row.reference_id;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s === '' ? null : s;
}

function StatementRowInvoiceCell({ row, fetchingOrderId, onOpen }) {
  const oid = extractOrderIdFromRow(row);
  if (!oid) return <span className="text-slate-400">—</span>;
  const invoicePathParam = extractReferenceIdFromRow(row) ?? oid;
  const loading = fetchingOrderId === invoicePathParam;
  return (
    <button
      type="button"
      onClick={() => onOpen(invoicePathParam)}
      disabled={loading}
      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-primary-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
      title={`Open invoice for order ${oid}`}
    >
      <i className={`material-icons-outlined text-base ${loading ? 'animate-spin' : ''}`}>
        {loading ? 'refresh' : 'download'}
      </i>
      Invoice
    </button>
  );
}

function displayNarration(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return NO_NARRATION;
  const meta = metadataAsObject(row.metadata);
  if (!meta) return NO_NARRATION;
  const raw = meta.narration;
  if (raw == null) return NO_NARRATION;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t === '' ? NO_NARRATION : t;
  }
  if (typeof raw === 'object') return JSON.stringify(raw);
  return String(raw);
}

const COLUMN_PRIORITY = [
  'posted_at_ms',
  'created_at',
  'created_at_ms',
  'timestamp',
  'occurred_at',
  'time',
  'id',
  'type',
  'transaction_type',
  'reference_type',
  'event_type',
  'reference_id',
  'reference',
  'amount_minor',
  'amount',
  'running_balance_minor',
  'balance_minor',
  'balance',
  'delta_minor',
  'description',
  'memo',
  'metadata',
  'narration',
];

function columnKeys(entries) {
  const set = new Set();
  entries.forEach((row) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      Object.keys(row).forEach((k) => set.add(k));
    }
  });
  const keys = [...set];
  keys.sort((a, b) => {
    const ia = COLUMN_PRIORITY.indexOf(a);
    const ib = COLUMN_PRIORITY.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return a.localeCompare(b);
  });
  return keys;
}

/** Single visible column: ledger line `id`; `reference_id` only if id is absent. */
const TRANSACTION_ID_COLUMN = '__transaction_id';

function transactionIdDisplay(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return '—';
  const id = row.id;
  if (id != null && String(id).trim() !== '') return String(id);
  const ref = row.reference_id;
  if (ref != null && String(ref).trim() !== '') return String(ref);
  return '—';
}

/** Replaces separate `id` and `reference_id` columns with one Transaction ID column. */
function statementDisplayKeys(entries) {
  const keys = columnKeys(entries);
  const hasId = keys.includes('id');
  const hasRef = keys.includes('reference_id');
  if (!hasId && !hasRef) return keys;

  const stripped = keys.filter((k) => k !== 'id' && k !== 'reference_id');
  const pos = hasId && hasRef
    ? Math.min(keys.indexOf('id'), keys.indexOf('reference_id'))
    : hasId
      ? keys.indexOf('id')
      : keys.indexOf('reference_id');
  const next = [...stripped];
  next.splice(pos, 0, TRANSACTION_ID_COLUMN);
  return next;
}

function humanizeKey(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Title-case phrase from snake_case (e.g. `payment_done` → `Payment Done`). */
function phraseFromSnake(s) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isDateTimeColumnKey(key) {
  if (!key || typeof key !== 'string') return false;
  const k = key.toLowerCase();
  // snake_case / API: *_at, *_time, *_ms, *_ts (exclude money-ish names)
  if (/_at$|_time$|_ms$|_ts$/.test(k)) {
    if (/amount|balance|minor|price|delta|quantity|fee|tax|discount|mrp|ptr|order_value/i.test(k)) {
      return false;
    }
    return true;
  }
  if (k === 'time' || k === 'ts' || k === 'epoch' || k.includes('timestamp') || k.includes('datetime')) {
    return true;
  }
  if (k.endsWith('_date') || k === 'date') return true;
  // camelCase: createdAt, updatedAt, eventTime, occurredAt, …
  if (/[a-z](?:At|On|Time|Date|Timestamp)$/i.test(key)) return true;
  return false;
}

function looksLikeIsoDateString(s) {
  const t = typeof s === 'string' ? s.trim() : '';
  return /^\d{4}-\d{2}-\d{2}(?:$|T|\s)/.test(t);
}

/** ~2000–2100 in ms or seconds */
function valueLooksLikeEpoch(value) {
  if (typeof value === 'bigint') {
    const n = Number(value);
    if (!Number.isSafeInteger(n)) return false;
    return valueLooksLikeEpoch(n);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e11 ? value * 1000 : value;
    return ms >= 946684800000 && ms < 4102444800000;
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (!/^-?\d{10,16}$/.test(t)) return false;
    const n = Number(t);
    if (!Number.isFinite(n)) return false;
    return valueLooksLikeEpoch(n);
  }
  return false;
}

function shouldFormatKeyAsMoney(key) {
  return key != null && /minor|amount|balance|delta|quantity|price|fee|tax|mrp|ptr/i.test(String(key));
}

/** Do not guess epoch for IDs, references, codes (numeric IDs can look like ms). */
function shouldNeverGuessEpochFromValue(key) {
  if (!key || typeof key !== 'string') return false;
  const k = key.toLowerCase();
  if (k === TRANSACTION_ID_COLUMN) return true;
  if (k === 'id' || k === 'reference_id' || k.endsWith('_id')) return true;
  if (/\b(uuid|sku|code|token|phone|pin|zip|iban|ifsc|gst)\b/i.test(k)) return true;
  return false;
}

function parseToDate(value) {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'bigint') {
    return parseToDate(Number(value));
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e11 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '') return null;
    if (/^-?\d{10,}$/.test(t)) {
      const n = Number(t);
      if (!Number.isFinite(n)) return null;
      const ms = n < 1e11 ? n * 1000 : n;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatHumanDateTime(value) {
  const d = parseToDate(value);
  if (!d) return null;
  const t = d.getTime();
  if (t < 946684800000 || t >= 4102444800000) return null;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'long',
      timeStyle: 'medium',
    }).format(d);
  } catch {
    return d.toLocaleString('en-IN');
  }
}

/** Descriptive table / export headers for known wallet API fields; unknown keys stay humanized. */
const COLUMN_TITLES = {
  created_at: 'Created',
  created_at_ms: 'Created',
  timestamp: 'Recorded',
  occurred_at: 'Occurred',
  time: 'Date and time',
  id: 'Line ID',
  transaction_type: 'Credit or debit',
  type: 'Transaction type',
  reference_type: 'Reference type',
  event_type: 'Wallet event type',
  reference_id: 'Reference ID',
  [TRANSACTION_ID_COLUMN]: 'Txn ID',
  reference: 'Reference',
  amount_minor: 'Amount (INR)',
  amount: 'Amount',
  balance_minor: 'Running balance (INR)',
  balance: 'Running balance',
  delta_minor: 'Balance change (INR)',
  description: 'Description',
  memo: 'Memo',
  metadata: 'Narration',
  narration: 'Narration',
  running_balance_minor: 'Running balance (INR)',
};

function columnTitle(key) {
  if (COLUMN_TITLES[key] != null) return COLUMN_TITLES[key];
  if (/_at$/i.test(key)) {
    const stem = key.replace(/_at$/i, '');
    return `${phraseFromSnake(stem)}`;
  }
  if (/_ms$/i.test(key) || /_ts$/i.test(key)) {
    const stem = key.replace(/_ms$|_ts$/i, '');
    return `${phraseFromSnake(stem)}`;
  }
  if (isDateTimeColumnKey(key)) {
    return `${phraseFromSnake(key)}`;
  }
  return humanizeKey(key);
}

function formatCellValue(key, value) {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'bigint') {
    return formatCellValue(key, Number(value));
  }

  if (shouldFormatKeyAsMoney(key) && typeof value === 'number') {
    return formatWalletBalanceMinor(value);
  }

  if (isDateTimeColumnKey(key)) {
    const human = formatHumanDateTime(value);
    if (human) return human;
  }
  if (typeof value === 'string' && looksLikeIsoDateString(value)) {
    const human = formatHumanDateTime(value);
    if (human) return human;
  }

  if (
    !shouldNeverGuessEpochFromValue(key) &&
    !shouldFormatKeyAsMoney(key) &&
    valueLooksLikeEpoch(value)
  ) {
    const human = formatHumanDateTime(value);
    if (human) return human;
  }

  return String(value);
}

/** Renders `metadata` column as `metadata.narration`; all other keys use normal formatting. */
function formatCellForColumn(row, key) {
  if (key === 'metadata') return displayNarration(row);
  if (key === TRANSACTION_ID_COLUMN) return transactionIdDisplay(row);
  return formatCellValue(key, row[key]);
}

function rowsForSheet(entries, keys) {
  return entries.map((row) => {
    const out = {};
    keys.forEach((k) => {
      if (k === 'metadata') {
        out[columnTitle(k)] = displayNarration(row);
        return;
      }
      if (k === TRANSACTION_ID_COLUMN) {
        out[columnTitle(k)] = transactionIdDisplay(row);
        return;
      }
      const raw = row && typeof row === 'object' ? row[k] : undefined;
      if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
        out[columnTitle(k)] = JSON.stringify(raw);
      } else if (typeof raw === 'number' && /minor|amount|balance|delta/i.test(k)) {
        out[columnTitle(k)] = Number(raw) / 100;
      } else if (
        isDateTimeColumnKey(k) ||
        (typeof raw === 'string' && looksLikeIsoDateString(raw)) ||
        (!shouldNeverGuessEpochFromValue(k) &&
          !shouldFormatKeyAsMoney(k) &&
          valueLooksLikeEpoch(raw))
      ) {
        out[columnTitle(k)] = formatCellValue(k, raw);
      } else {
        out[columnTitle(k)] = raw ?? '';
      }
    });
    return out;
  });
}

function WalletStatementPage() {
  const { walletId: walletIdParam } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  /** From `GET …/wallet/:wallet_id` — owner + currency line for the header. */
  const [walletSubtitle, setWalletSubtitle] = useState('');
  /** From `GET …/wallet/:wallet_id` (available balance and credit line fields). */
  const [walletSummary, setWalletSummary] = useState(null);
  const [internalFeatures, setInternalFeatures] = useState(null);

  const handlePingResult = useCallback((_status, pingPayload) => {
    const f = pingPayload?.features;
    setInternalFeatures(f && typeof f === 'object' ? f : null);
  }, []);

  const [limit, setLimit] = useState(20);
  /** `GET …/statement` cursor: pass back as `cursor` query (JSON string); `null` = first page */
  const [statementCursor, setStatementCursor] = useState(null);
  /** Stack of cursors for the “previous” page (values we had before each “next”) */
  const [cursorBackStack, setCursorBackStack] = useState([]);

  const [fromLocal, setFromLocal] = useState('');
  const [toLocal, setToLocal] = useState('');
  const [referenceType, setReferenceType] = useState('');
  const [eventType, setEventType] = useState('');
  const [transactionType, setTransactionType] = useState('');

  const [applied, setApplied] = useState({
    fromLocal: '',
    toLocal: '',
    referenceType: '',
    eventType: '',
    transactionType: '',
  });
  const [invoiceFetchOrderId, setInvoiceFetchOrderId] = useState(null);
  const [depositModalOpen, setDepositModalOpen] = useState(false);

  useEffect(() => {
    setCursorBackStack([]);
    setStatementCursor(null);
    setWalletSubtitle('');
    setWalletSummary(null);
    setDepositModalOpen(false);
  }, [walletIdParam]);

  const queryParams = useMemo(() => {
    const p = { limit };
    if (statementCursor) p.cursor = JSON.stringify(statementCursor);
    if (applied.fromLocal) p.from_ms = String(new Date(applied.fromLocal).getTime());
    if (applied.toLocal) p.to_ms = String(new Date(applied.toLocal).getTime());
    if (applied.referenceType.trim()) p.reference_type = applied.referenceType.trim();
    if (applied.eventType.trim()) p.event_type = applied.eventType.trim();
    if (applied.transactionType === 'credit' || applied.transactionType === 'debit') {
      p.transaction_type = applied.transactionType;
    }
    return p;
  }, [limit, statementCursor, applied]);

  const load = useCallback(async () => {
    if (!walletIdParam) return;
    setLoading(true);
    setError(null);
    try {
      const [stmtRes, walletRes] = await Promise.allSettled([
        getWalletStatement(walletIdParam, queryParams),
        getWallet(walletIdParam),
      ]);

      if (stmtRes.status === 'fulfilled') {
        const data = stmtRes.value;
        setPayload(data);
      } else {
        console.error(stmtRes.reason);
        setPayload(null);
        setError(getAxiosErrorMessage(stmtRes.reason, 'Failed to load wallet statement'));
      }

      if (walletRes.status === 'fulfilled') {
        const data = walletRes.value;
        if (data && typeof data === 'object') {
          setWalletSummary(data);
          const label = walletSummaryLabel(data);
          if (label) setWalletSubtitle(label);
        }
      } else {
        setWalletSummary(null);
      }
    } finally {
      setLoading(false);
    }
  }, [walletIdParam, queryParams]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (
      !walletSummary ||
      String(walletSummary.currency ?? '').trim().toUpperCase() !== 'CASH' ||
      window.location.hash !== '#cash-receive-qr'
    ) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      document.getElementById('cash-receive-qr')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(id);
  }, [walletSummary]);

  const entries = useMemo(() => extractStatementEntries(payload || {}), [payload]);
  const keys = useMemo(() => statementDisplayKeys(entries), [entries]);
  const showInvoiceColumn = useMemo(() => entries.some((row) => extractOrderIdFromRow(row)), [entries]);

  const summaryCurrency =
    walletSummary && typeof walletSummary.currency === 'string' && walletSummary.currency.trim() !== ''
      ? walletSummary.currency.trim()
      : 'INR';

  const isCashCreditWallet = String(summaryCurrency).trim().toUpperCase() === 'CASH';

  const hasDepositOptions = useMemo(
    () => parseDepositInstructionOptions(walletSummary?.deposit_instructions).length > 0,
    [walletSummary?.deposit_instructions],
  );

  useEffect(() => {
    if (!depositModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setDepositModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [depositModalOpen]);

  const statementOpeningMinor = useMemo(
    () => statementEnvelopeMinor(payload, ['opening_balance_minor', 'opening_balance']),
    [payload],
  );
  const statementClosingMinor = useMemo(
    () => statementEnvelopeMinor(payload, ['closing_balance_minor', 'closing_balance']),
    [payload],
  );

  const openOrderInvoice = async (referenceId) => {
    if (!walletIdParam || !referenceId) return;
    setInvoiceFetchOrderId(referenceId);
    try {
      const data = await getWalletInvoice(walletIdParam, referenceId);
      const url = data && typeof data === 'object' && typeof data.invoice_url === 'string' ? data.invoice_url : null;
      if (typeof url === 'string' && url.trim() !== '') {
        window.open(url.trim(), '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Invoice URL was not returned for this order.');
      }
    } catch (e) {
      console.error(e);
      toast.error(getAxiosErrorMessage(e, 'Failed to open invoice'));
    } finally {
      setInvoiceFetchOrderId(null);
    }
  };

  const canGoNext =
    payload != null &&
    payload.next_cursor != null &&
    typeof payload.next_cursor === 'object' &&
    !Array.isArray(payload.next_cursor);

  const statementPageNumber = cursorBackStack.length + 1;

  const goNextPage = () => {
    if (!payload?.next_cursor || typeof payload.next_cursor !== 'object' || Array.isArray(payload.next_cursor)) {
      return;
    }
    setCursorBackStack((s) => [...s, statementCursor]);
    setStatementCursor(payload.next_cursor);
  };

  const goPrevPage = () => {
    setCursorBackStack((s) => {
      if (s.length === 0) return s;
      const prev = s[s.length - 1];
      setStatementCursor(prev ?? null);
      return s.slice(0, -1);
    });
  };

  const applyFilters = () => {
    setApplied({
      fromLocal,
      toLocal,
      referenceType,
      eventType,
      transactionType,
    });
    setCursorBackStack([]);
    setStatementCursor(null);
  };

  const buildExportParams = () => {
    const p = { limit: 500 };
    if (applied.fromLocal) p.from_ms = String(new Date(applied.fromLocal).getTime());
    if (applied.toLocal) p.to_ms = String(new Date(applied.toLocal).getTime());
    if (applied.referenceType.trim()) p.reference_type = applied.referenceType.trim();
    if (applied.eventType.trim()) p.event_type = applied.eventType.trim();
    if (applied.transactionType === 'credit' || applied.transactionType === 'debit') {
      p.transaction_type = applied.transactionType;
    }
    return p;
  };

  const fetchExportEntries = async () => {
    const data = await getWalletStatement(walletIdParam, buildExportParams());
    return extractStatementEntries(data);
  };

  const downloadExcel = async () => {
    try {
      const rows = await fetchExportEntries();
      const k = statementDisplayKeys(rows);
      if (k.length === 0) {
        const ws = XLSX.utils.aoa_to_sheet([['No transactions for these filters']]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
        XLSX.writeFile(wb, `wallet-statement-${walletIdParam}.xlsx`);
        return;
      }
      const sheetRows = rowsForSheet(rows, k);
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
      const safeName = (walletSubtitle || 'wallet').replace(/[^\w-]+/g, '_');
      XLSX.writeFile(wb, `wallet-statement-${safeName}-${walletIdParam}.xlsx`);
    } catch (e) {
      console.error(e);
      setError(getAxiosErrorMessage(e, 'Excel export failed'));
    }
  };

  const downloadPdf = async () => {
    try {
      const rows = await fetchExportEntries();
      const k = statementDisplayKeys(rows);
      const title = document.createElement('div');
      title.style.padding = '16px 16px 8px';
      title.style.fontFamily = 'system-ui, sans-serif';
      title.innerHTML = `<h1 style="font-size:16px;margin:0;">Statement</h1>
        <p style="margin:6px 0 0;font-size:12px;color:#444;">${walletSubtitle ? `${walletSubtitle} · ` : ''}${walletIdParam}</p>
        <p style="margin:4px 0 0;font-size:11px;color:#666;">Up to 500 lines · same filters as the screen</p>`;

      const table = document.createElement('table');
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.fontSize = '11px';
      table.style.fontFamily = 'system-ui, sans-serif';

      const thead = document.createElement('thead');
      const hr = document.createElement('tr');
      k.forEach((key) => {
        const th = document.createElement('th');
        th.textContent = columnTitle(key);
        th.style.textAlign = 'left';
        th.style.borderBottom = '1px solid #ccc';
        th.style.padding = '6px 8px';
        th.style.background = '#f8fafc';
        hr.appendChild(th);
      });
      thead.appendChild(hr);
      table.appendChild(thead);

      const tbody = document.createElement('tbody');
      rows.forEach((row) => {
        const tr = document.createElement('tr');
        k.forEach((key) => {
          const td = document.createElement('td');
          td.textContent = formatCellForColumn(row, key);
          td.style.borderBottom = '1px solid #eee';
          td.style.padding = '6px 8px';
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);

      const wrap = document.createElement('div');
      wrap.style.background = '#fff';
      wrap.style.width = '1100px';
      wrap.appendChild(title);
      wrap.appendChild(table);
      wrap.style.position = 'fixed';
      wrap.style.left = '-10000px';
      wrap.style.top = '0';
      document.body.appendChild(wrap);

      try {
        const canvas = await html2canvas(wrap, {
          scale: 2,
          backgroundColor: '#ffffff',
          windowWidth: wrap.offsetWidth,
        });
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 595;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const pdf = new jsPDF({
          orientation: imgHeight > imgWidth ? 'portrait' : 'landscape',
          unit: 'pt',
          format: [imgWidth, imgHeight],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        const safeName = (walletSubtitle || 'wallet').replace(/[^\w-]+/g, '_');
        pdf.save(`wallet-statement-${safeName}-${walletIdParam}.pdf`);
      } finally {
        document.body.removeChild(wrap);
      }
    } catch (e) {
      console.error(e);
      setError(getAxiosErrorMessage(e, 'PDF export failed'));
    }
  };

  const walletIdFull =
    walletSummary && typeof walletSummary.wallet_id === 'string' && walletSummary.wallet_id.trim() !== ''
      ? walletSummary.wallet_id
      : walletIdParam;

  return (
    <div>
      <header className="mb-4">
        <Link to="/wallets" className="link-back mb-2">
          <i className="material-icons-outlined text-base">arrow_back</i>
          All wallets
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">Statement</h1>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-1.5">
            <button
              type="button"
              onClick={load}
              className="btn-secondary inline-flex items-center gap-1.5 py-1.5 pl-2.5 pr-3 text-sm"
              disabled={loading}
              title="Refresh"
            >
              <i className={`material-icons-outlined text-base ${loading ? 'animate-spin' : ''}`}>refresh</i>
              Refresh
            </button>
            {walletIdParam && hasDepositOptions ? (
              <button
                type="button"
                onClick={() => setDepositModalOpen(true)}
                className="btn-secondary inline-flex items-center gap-1.5 py-1.5 pl-2.5 pr-3 text-sm"
                title="Bank and UPI details to add funds to this wallet"
              >
                <i className="material-icons-outlined text-base">payments</i>
                Deposit options
              </button>
            ) : null}
            {isCashCreditWallet && walletIdParam ? (
              <Link
                to={`/wallets/${encodeURIComponent(walletIdFull)}/repayment-request`}
                className="btn-secondary inline-flex items-center gap-1.5 border-amber-200 bg-amber-50 py-1.5 pl-2.5 pr-3 text-sm text-amber-900 hover:bg-amber-100"
                title={`Submit a repayment request for this wallet (${walletIdFull})`}
              >
                <i className="material-icons-outlined text-base">request_quote</i>
                Request repayment
              </Link>
            ) : null}
            {isCashCreditWallet && walletIdParam && internalFeatures?.wallet_credit_issue_requests === true ? (
              <Link
                to={`/wallets/credit-issue-requests?wallet_id=${encodeURIComponent(walletIdFull)}`}
                className="btn-secondary inline-flex items-center gap-1.5 border-sky-200 bg-sky-50 py-1.5 pl-2.5 pr-3 text-sm text-sky-900 hover:bg-sky-100"
                title={`Review credit issue requests for this wallet (${walletIdFull})`}
              >
                <i className="material-icons-outlined text-base">fact_check</i>
                Credit requests
              </Link>
            ) : null}
            <button
              type="button"
              onClick={downloadPdf}
              className="btn-secondary inline-flex items-center gap-1.5 py-1.5 pl-2.5 pr-3 text-sm"
              disabled={loading || !!error}
              title="Download statement as PDF"
            >
              <i className="material-icons-outlined text-base">picture_as_pdf</i>
              PDF
            </button>
            <button
              type="button"
              onClick={downloadExcel}
              className="btn-primary inline-flex items-center gap-1.5 py-1.5 pl-2.5 pr-3 text-sm"
              disabled={loading || !!error}
              title="Download statement as Excel"
            >
              <i className="material-icons-outlined text-base">table_chart</i>
              Excel
            </button>
            <InternalB2BWalletOps walletId={walletIdParam} onOperationSuccess={load} onPingResult={handlePingResult} />
          </div>
        </div>
      </header>

      {walletIdParam ? (
        <>
        <div className="mb-4 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Wallet details</p>
          <dl className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="min-w-0">
              <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Wallet ID</dt>
              <dd className="mt-0.5 break-all font-mono text-xs leading-snug text-slate-900">{walletIdFull}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Owner type</dt>
              <dd className="mt-0.5 text-sm text-slate-900">
                {walletSummary && walletSummary.owner_type != null && String(walletSummary.owner_type).trim() !== ''
                  ? String(walletSummary.owner_type)
                  : '—'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Owner ID</dt>
              <dd className="mt-0.5 break-all font-mono text-xs text-slate-800">
                {walletSummary && walletSummary.owner_id != null && String(walletSummary.owner_id).trim() !== ''
                  ? String(walletSummary.owner_id)
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Currency</dt>
              <dd className="mt-0.5 text-sm text-slate-900">
                {walletSummary && typeof walletSummary.currency === 'string' && walletSummary.currency.trim() !== ''
                  ? String(walletSummary.currency).trim().toUpperCase() === 'CASH'
                    ? 'CASH (cash credit)'
                    : walletSummary.currency.trim()
                  : '—'}
              </dd>
            </div>
          </dl>
          {(walletSummary ||
            (!loading && !error && (statementOpeningMinor != null || statementClosingMinor != null))) && (
            <div
              className="mt-3 grid gap-x-4 gap-y-3 border-t border-slate-100 pt-3 [grid-template-columns:repeat(auto-fit,minmax(9.25rem,1fr))]"
            >
              {walletSummary ? (
                <>
                  <div className="min-w-0">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Available</p>
                    <p className="tabular-nums text-sm font-semibold text-emerald-700">
                      {formatWalletBalanceMinor(walletSummary.balance, summaryCurrency)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      {isCashCreditWallet ? 'COD credit limit' : 'Credit limit'}
                    </p>
                    <p className="tabular-nums text-sm font-semibold text-slate-800">
                      {formatWalletBalanceMinor(walletSummary.credit_line_limit_minor, summaryCurrency)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      {isCashCreditWallet ? 'Credit in use' : 'Credit used'}
                    </p>
                    <p className="tabular-nums text-sm font-semibold text-red-700">
                      {formatWalletBalanceMinor(walletSummary.credit_line_depleted_minor, summaryCurrency)}
                    </p>
                  </div>
                </>
              ) : null}
              {!loading && !error && statementOpeningMinor != null ? (
                <div className="min-w-0">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Opening</p>
                  <p className="tabular-nums text-sm font-semibold text-slate-800">
                    {formatWalletBalanceMinor(statementOpeningMinor, summaryCurrency)}
                  </p>
                </div>
              ) : null}
              {!loading && !error && statementClosingMinor != null ? (
                <div className="min-w-0">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Closing</p>
                  <p className="tabular-nums text-sm font-semibold text-slate-800">
                    {formatWalletBalanceMinor(statementClosingMinor, summaryCurrency)}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>
        {walletSummary && String(walletSummary.currency ?? '').trim().toUpperCase() === 'CASH' ? (
          <div className="mb-4 space-y-3">
            <p className="rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-2 text-sm text-slate-700">
              This is a <span className="font-medium text-slate-900">cash-credit</span> wallet: each COD order is paid from
              your line while you collect physical cash from the customer. When you hand that cash to your lead, they
              should transfer credit from their wallet to yours. The pattern continues until money reaches the
              company&apos;s account and the company issues repayment here. Use <span className="font-medium">Request
              repayment</span> in the toolbar above to submit a repayment request for{' '}
              <span className="font-mono text-xs text-slate-800">{walletIdFull}</span>. Use the QR below for incoming
              credit from whoever received your physical cash.
            </p>
            <CashWalletReceiveQr walletId={walletIdFull} />
          </div>
        ) : null}

        {depositModalOpen && walletSummary && hasDepositOptions ? (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wallet-deposit-modal-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) setDepositModalOpen(false);
            }}
          >
            <div
              className="card-surface max-h-[90vh] w-full max-w-lg overflow-y-auto p-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-2">
                <h2 id="wallet-deposit-modal-title" className="text-base font-semibold text-slate-900">
                  Deposit options
                </h2>
                <button
                  type="button"
                  onClick={() => setDepositModalOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                  aria-label="Close"
                >
                  <i className="material-icons-outlined">close</i>
                </button>
              </div>
              <div className="mt-3">
                <WalletDepositInstructions
                  depositInstructions={walletSummary.deposit_instructions}
                  embedded
                />
              </div>
            </div>
          </div>
        ) : null}
        </>
      ) : null}

      <details className="card-surface mb-4 overflow-hidden">
        <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50/80 [&::-webkit-details-marker]:hidden">
          <span className="inline-flex items-center gap-1.5">
            <i className="material-icons-outlined text-lg text-slate-500">tune</i>
            Filters
          </span>
        </summary>
        <div className="border-t border-slate-100 px-3 pb-3 pt-1">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="flex flex-col gap-0.5 text-xs">
              <span className="text-slate-500">From</span>
              <input
                type="datetime-local"
                value={fromLocal}
                onChange={(e) => setFromLocal(e.target.value)}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-xs">
              <span className="text-slate-500">To</span>
              <input
                type="datetime-local"
                value={toLocal}
                onChange={(e) => setToLocal(e.target.value)}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-xs">
              <span className="text-slate-500">Credit / debit</span>
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">All</option>
                <option value="credit">Credit</option>
                <option value="debit">Debit</option>
              </select>
            </label>
            <label className="flex flex-col gap-0.5 text-xs">
              <span className="text-slate-500">Reference type</span>
              <input
                type="text"
                value={referenceType}
                onChange={(e) => setReferenceType(e.target.value)}
                placeholder="e.g. order"
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-xs">
              <span className="text-slate-500">Event type</span>
              <input
                type="text"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                placeholder="Ledger event"
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={applyFilters} className="btn-primary w-full py-2 text-sm sm:w-auto">
                Apply
              </button>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Exports use these filters (up to 500 lines).</p>
        </div>
      </details>

      <div className="card-surface overflow-hidden">
        {loading && (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-slate-100" />
            ))}
          </div>
        )}
        {!loading && error && (
          <div className="flex items-start gap-3 border-l-4 border-red-500 bg-red-50 p-4 text-red-800">
            <i className="material-icons-outlined shrink-0">error_outline</i>
            <div>
              <p className="font-medium">Could not load statement</p>
              <p className="mt-1 text-sm opacity-90">{error}</p>
            </div>
          </div>
        )}
        {!loading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead>
                  <tr className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {keys.length === 0 ? (
                      <>
                        <th className="px-3 py-2">Raw transaction row</th>
                        {showInvoiceColumn ? (
                          <th className="whitespace-nowrap px-3 py-2">Invoice</th>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {keys.map((colKey) => (
                          <th key={colKey} className="whitespace-nowrap px-3 py-2">
                            {columnTitle(colKey)}
                          </th>
                        ))}
                        {showInvoiceColumn ? (
                          <th className="whitespace-nowrap px-3 py-2">Invoice</th>
                        ) : null}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {entries.length === 0 && (
                    <tr>
                      <td
                        colSpan={Math.max(keys.length, 1) + (showInvoiceColumn ? 1 : 0)}
                        className="px-3 py-10 text-center text-slate-500"
                      >
                        <i className="material-icons-outlined mb-2 text-4xl text-slate-200">inbox</i>
                        <p>No transactions for this page or filters.</p>
                      </td>
                    </tr>
                  )}
                  {entries.map((row, idx) => (
                    <tr
                      key={`${transactionIdDisplay(row)}-${idx}`}
                      className="hover:bg-slate-50/80"
                    >
                      {keys.length === 0 ? (
                        <>
                          <td className="px-3 py-2 font-mono text-xs text-slate-600">
                            {JSON.stringify(row)}
                          </td>
                          {showInvoiceColumn ? (
                            <td className="whitespace-nowrap px-3 py-2 text-sm">
                              <StatementRowInvoiceCell
                                row={row}
                                fetchingOrderId={invoiceFetchOrderId}
                                onOpen={openOrderInvoice}
                              />
                            </td>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {keys.map((colKey) => {
                            const cellText = formatCellForColumn(row, colKey);
                            const muted =
                              colKey === 'metadata' && cellText === NO_NARRATION;
                            const fullTxnId =
                              colKey === TRANSACTION_ID_COLUMN ? transactionIdDisplay(row) : null;
                            return (
                              <td
                                key={colKey}
                                className={
                                  colKey === TRANSACTION_ID_COLUMN
                                    ? 'max-w-[11rem] px-3 py-2 font-mono text-xs text-slate-800'
                                    : 'max-w-xs break-words px-3 py-2 text-sm text-slate-800'
                                }
                              >
                                {colKey === TRANSACTION_ID_COLUMN && fullTxnId && fullTxnId !== '—' ? (
                                  <span title={fullTxnId}>{truncateMiddle(fullTxnId, 8, 6)}</span>
                                ) : (
                                  <span className={muted ? 'italic text-slate-400' : ''}>{cellText}</span>
                                )}
                              </td>
                            );
                          })}
                          {showInvoiceColumn ? (
                            <td className="whitespace-nowrap px-3 py-2 text-sm">
                              <StatementRowInvoiceCell
                                row={row}
                                fetchingOrderId={invoiceFetchOrderId}
                                onOpen={openOrderInvoice}
                              />
                            </td>
                          ) : null}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span>
                  Page <span className="font-semibold text-slate-900">{statementPageNumber}</span>
                </span>
                <span className="text-slate-500">
                  · {entries.length} {entries.length === 1 ? 'line' : 'lines'}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  Rows
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCursorBackStack([]);
                      setStatementCursor(null);
                    }}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-slate-900"
                  >
                    {[10, 20, 50, 100, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={cursorBackStack.length === 0}
                  onClick={goPrevPage}
                >
                  Previous
                </button>
                <button type="button" className="btn-secondary text-sm" disabled={!canGoNext} onClick={goNextPage}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default WalletStatementPage;
